/* eslint-disable */
import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  styled,
  useTheme,
} from "@mui/material";
import {
  Add as AddIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  Assessment as AssessmentIcon,
  EmojiEvents as TrophyIcon,
  CompareArrows as CompareArrowsIcon,
} from "@mui/icons-material";
import { useLazyQuery, useMutation } from "@apollo/client";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import { GET_AB_TESTS } from "../../gql/queries/ai";
import { CREATE_AB_TEST, START_AB_TEST, STOP_AB_TEST, EVALUATE_AB_TEST, DELETE_AB_TEST } from "../../gql/mutations/ai";
import AiPageHeader from "./AiPageHeader";

const SectionWrapper = styled("section")(({ theme }) => ({
  minHeight: "calc(100vh - 70px)",
  backgroundColor: theme.palette.background.default,
  paddingBottom: "60px",
}));

function AiAbTesting() {
  const theme = useTheme();
  const userData = useSelector(state => state.reducer.userData);
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get("projectId") || "";

  const [localProjectId, setLocalProjectId] = useState(projectId || "");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newTestUrl, setNewTestUrl] = useState("");
  const [newTestType, setNewTestType] = useState("title");
  const [expandedTest, setExpandedTest] = useState(null);

  const [fetchTests, { data: testsData, loading: testsLoading }] = useLazyQuery(GET_AB_TESTS, { fetchPolicy: "network-only" });

  const [createTest, { loading: createLoading }] = useMutation(CREATE_AB_TEST, {
    onCompleted: () => {
      toast.success("A/B test created successfully!");
      setNewTestUrl("");
      setNewTestType("title");
      setShowCreateForm(false);
      handleFetchTests();
    },
    onError: err => {
      toast.error(err.message || "Failed to create test");
    },
  });

  const [startTest, { loading: startLoading }] = useMutation(START_AB_TEST, {
    onCompleted: () => {
      toast.success("Test started!");
      handleFetchTests();
    },
    onError: err => {
      toast.error(err.message || "Failed to start test");
    },
  });

  const [stopTest, { loading: stopLoading }] = useMutation(STOP_AB_TEST, {
    onCompleted: () => {
      toast.success("Test stopped!");
      handleFetchTests();
    },
    onError: err => {
      toast.error(err.message || "Failed to stop test");
    },
  });

  const [evaluateTest, { loading: evaluateLoading }] = useMutation(EVALUATE_AB_TEST, {
    onCompleted: () => {
      toast.success("Test evaluated successfully!");
      handleFetchTests();
    },
    onError: err => {
      toast.error(err.message || "Failed to evaluate test");
    },
  });

  const [deleteTest, { loading: deleteLoading }] = useMutation(DELETE_AB_TEST, {
    onCompleted: () => {
      toast.success("Test deleted!");
      handleFetchTests();
    },
    onError: err => {
      toast.error(err.message || "Failed to delete test");
    },
  });

  const activeProjectId = projectId || localProjectId;

  const handleFetchTests = () => {
    if (activeProjectId) {
      fetchTests({ variables: { projectId: activeProjectId } });
    }
  };

  useEffect(() => {
    if (projectId) setLocalProjectId(projectId);
  }, [projectId]);

  useEffect(() => {
    if (activeProjectId) {
      handleFetchTests();
    }
  }, [activeProjectId]);

  const handleCreate = () => {
    if (!activeProjectId) {
      toast.error("Please provide a Project ID");
      return;
    }
    if (!newTestUrl.trim()) {
      toast.error("Please enter a URL");
      return;
    }
    createTest({
      variables: {
        data: {
          projectId: activeProjectId,
          url: newTestUrl.trim(),
          testType: newTestType,
        },
      },
    });
  };

  const handleStart = id => {
    startTest({ variables: { id } });
  };

  const handleStop = id => {
    stopTest({ variables: { id } });
  };

  const handleEvaluate = id => {
    evaluateTest({ variables: { id } });
  };

  const handleDelete = id => {
    deleteTest({ variables: { id } });
  };

  const statusConfig = {
    draft: { color: "default", label: "Draft" },
    active: { color: "success", label: "Active" },
    completed: { color: "info", label: "Completed" },
    paused: { color: "warning", label: "Paused" },
  };

  const getStatusChip = status => {
    const config = statusConfig[status] || { color: "default", label: status };
    return <Chip label={config.label} size="small" color={config.color} />;
  };

  const parseJSON = value => {
    try {
      return typeof value === "string" ? JSON.parse(value) : value;
    } catch {
      return value;
    }
  };

  const formatDate = dateStr => {
    if (!dateStr) return "---";
    try {
      return new Date(Number(dateStr) || dateStr).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  const tests = testsData?.getAbTests || [];
  const anyActionLoading = startLoading || stopLoading || evaluateLoading || deleteLoading;

  return (
    <SectionWrapper>
      <Container maxWidth="">
        <Box sx={{ flexGrow: 1 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 2, mb: 2 }}>
            <AiPageHeader
              title="A/B Testing"
              description="Create and manage SEO A/B tests for titles and meta descriptions."
              projectId={activeProjectId}
              onProjectChange={id => setLocalProjectId(id || "")}
              showProjectSelector
              showQuickLinks
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowCreateForm(true)}
              disabled={!activeProjectId}
              sx={{ textTransform: "none", alignSelf: "flex-end" }}
            >
              Create Test
            </Button>
          </Box>

          {/* Create Test Dialog */}
          <Dialog open={showCreateForm} onClose={() => setShowCreateForm(false)} maxWidth="sm" fullWidth>
            <DialogTitle
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Create A/B Test
              </Typography>
              <IconButton size="small" onClick={() => setShowCreateForm(false)}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ pt: 1 }}>
                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>
                  URL to Test
                </Typography>
                <TextField size="small" fullWidth placeholder="https://example.com/page" value={newTestUrl} onChange={e => setNewTestUrl(e.target.value)} sx={{ mb: 2 }} />
                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Test Type
                </Typography>
                <Select size="small" fullWidth value={newTestType} onChange={e => setNewTestType(e.target.value)}>
                  <MenuItem value="title">Title Tag</MenuItem>
                  <MenuItem value="meta_description">Meta Description</MenuItem>
                </Select>
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={() => setShowCreateForm(false)} sx={{ textTransform: "none" }}>
                Cancel
              </Button>
              <Button variant="contained" onClick={handleCreate} disabled={createLoading} sx={{ textTransform: "none" }}>
                {createLoading ? <CircularProgress size={22} color="inherit" /> : "Create"}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Tests List */}
          <Grid container spacing={2} mt={1}>
            {testsLoading ? (
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: 200,
                  }}
                >
                  <CircularProgress />
                </Box>
              </Grid>
            ) : tests.length === 0 ? (
              <Grid item xs={12}>
                <Box sx={{ textAlign: "center", py: 8 }}>
                  <CompareArrowsIcon sx={{ fontSize: 64, color: "text.disabled", mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                    No A/B tests yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {activeProjectId
                      ? "Create an A/B test to compare title or meta description variants and improve CTR."
                      : "Select a website above to create A/B tests for that project."}
                  </Typography>
                  {activeProjectId && (
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => setShowCreateForm(true)} sx={{ textTransform: "none" }}>
                      Create Test
                    </Button>
                  )}
                </Box>
              </Grid>
            ) : (
              tests.map(test => {
                const variants = parseJSON(test.variants);
                const results = parseJSON(test.results);
                const variantCount = Array.isArray(variants) ? variants.length : 0;
                const isExpanded = expandedTest === test.id;

                return (
                  <Grid item xs={12} md={6} key={test.id}>
                    <Card
                      sx={{
                        border: `1px solid ${theme.palette.divider}`,
                        boxShadow: "none",
                        transition: "border-color 0.2s",
                        "&:hover": {
                          borderColor: theme.palette.primary.main,
                        },
                      }}
                    >
                      <CardContent>
                        {/* Header */}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            mb: 1.5,
                          }}
                        >
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                              title={test.url}
                            >
                              {test.url}
                            </Typography>
                          </Box>
                          {getStatusChip(test.status)}
                        </Box>

                        {/* Details Row */}
                        <Box
                          sx={{
                            display: "flex",
                            gap: 2,
                            flexWrap: "wrap",
                            mb: 1.5,
                          }}
                        >
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Test Type
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {test.testType === "meta_description" ? "Meta Description" : "Title Tag"}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Variants
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {variantCount}
                            </Typography>
                          </Box>
                          {test.winnerIndex !== null && test.winnerIndex !== undefined && (
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Winner
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <TrophyIcon sx={{ fontSize: 16, color: "#FFD700" }} />
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  Variant {test.winnerIndex + 1}
                                </Typography>
                              </Box>
                            </Box>
                          )}
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Created
                            </Typography>
                            <Typography variant="body2">{formatDate(test.createdAt)}</Typography>
                          </Box>
                        </Box>

                        {/* Action Buttons */}
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            flexWrap: "wrap",
                            mt: 2,
                            pt: 1.5,
                            borderTop: `1px solid ${theme.palette.divider}`,
                          }}
                        >
                          {test.status === "draft" && (
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              startIcon={<PlayArrowIcon />}
                              onClick={() => handleStart(test.id)}
                              disabled={anyActionLoading}
                              sx={{ textTransform: "none" }}
                            >
                              Start
                            </Button>
                          )}
                          {test.status === "active" && (
                            <>
                              <Button
                                size="small"
                                variant="contained"
                                color="warning"
                                startIcon={<StopIcon />}
                                onClick={() => handleStop(test.id)}
                                disabled={anyActionLoading}
                                sx={{ textTransform: "none" }}
                              >
                                Stop
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<AssessmentIcon />}
                                onClick={() => handleEvaluate(test.id)}
                                disabled={anyActionLoading}
                                sx={{ textTransform: "none" }}
                              >
                                Evaluate
                              </Button>
                            </>
                          )}
                          {(test.status === "completed" || results) && (
                            <Button size="small" variant="outlined" onClick={() => setExpandedTest(isExpanded ? null : test.id)} sx={{ textTransform: "none" }}>
                              {isExpanded ? "Hide Results" : "View Results"}
                            </Button>
                          )}
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={() => handleDelete(test.id)}
                            disabled={anyActionLoading}
                            sx={{ textTransform: "none", ml: "auto" }}
                          >
                            Delete
                          </Button>
                        </Box>

                        {/* Expanded Results */}
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box
                            sx={{
                              mt: 2,
                              pt: 2,
                              borderTop: `1px solid ${theme.palette.divider}`,
                            }}
                          >
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                              Test Results
                            </Typography>

                            {test.originalValue && (
                              <Box sx={{ mb: 1.5 }}>
                                <Typography variant="caption" color="text.secondary">
                                  Original Value
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {test.originalValue}
                                </Typography>
                              </Box>
                            )}

                            {/* Variants Table */}
                            {Array.isArray(variants) && variants.length > 0 && (
                              <Table size="small" sx={{ mb: 1 }}>
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>Variant</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Value</TableCell>
                                    {results && Array.isArray(results) && (
                                      <>
                                        <TableCell sx={{ fontWeight: 600 }} align="right">
                                          Impressions
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 600 }} align="right">
                                          Clicks
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 600 }} align="right">
                                          CTR
                                        </TableCell>
                                      </>
                                    )}
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {variants.map((variant, idx) => {
                                    const variantResult = results && Array.isArray(results) ? results[idx] : null;
                                    const isWinner = test.winnerIndex !== null && test.winnerIndex !== undefined && test.winnerIndex === idx;
                                    return (
                                      <TableRow
                                        key={idx}
                                        sx={{
                                          backgroundColor: isWinner ? (theme.palette.mode === "dark" ? "#4caf5020" : "#e8f5e9") : "inherit",
                                        }}
                                      >
                                        <TableCell>
                                          <Box
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 0.5,
                                            }}
                                          >
                                            Variant {idx + 1}
                                            {isWinner && (
                                              <TrophyIcon
                                                sx={{
                                                  fontSize: 14,
                                                  color: "#FFD700",
                                                }}
                                              />
                                            )}
                                          </Box>
                                        </TableCell>
                                        <TableCell>
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              maxWidth: 200,
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                              whiteSpace: "nowrap",
                                            }}
                                            title={typeof variant === "string" ? variant : variant.value || JSON.stringify(variant)}
                                          >
                                            {typeof variant === "string" ? variant : variant.value || JSON.stringify(variant)}
                                          </Typography>
                                        </TableCell>
                                        {results && Array.isArray(results) && (
                                          <>
                                            <TableCell align="right">{variantResult?.impressions ?? "---"}</TableCell>
                                            <TableCell align="right">{variantResult?.clicks ?? "---"}</TableCell>
                                            <TableCell align="right">{variantResult?.ctr != null ? `${(variantResult.ctr * 100).toFixed(2)}%` : "---"}</TableCell>
                                          </>
                                        )}
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            )}

                            {/* Dates */}
                            <Box
                              sx={{
                                display: "flex",
                                gap: 3,
                                mt: 1,
                              }}
                            >
                              {test.startDate && (
                                <Box>
                                  <Typography variant="caption" color="text.secondary">
                                    Start Date
                                  </Typography>
                                  <Typography variant="body2">{formatDate(test.startDate)}</Typography>
                                </Box>
                              )}
                              {test.endDate && (
                                <Box>
                                  <Typography variant="caption" color="text.secondary">
                                    End Date
                                  </Typography>
                                  <Typography variant="body2">{formatDate(test.endDate)}</Typography>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        </Collapse>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })
            )}
          </Grid>
        </Box>
      </Container>
    </SectionWrapper>
  );
}

export default AiAbTesting;
